import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';
import WorksheetForm from './WorksheetForm';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status }) {
  const styles = {
    not_started: { color: C.muted,    bg: '#eeeeee' },
    submitted:   { color: '#155724',  bg: '#d4edda' },
    reviewed:    { color: '#004085',  bg: '#cce5ff' },
  };
  const s = styles[status] ?? styles.not_started;
  const label = status === 'not_started' ? 'Not Started'
    : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, color: s.color, background: s.bg,
    }}>
      {label}
    </span>
  );
}

// ─── Member view ──────────────────────────────────────────────────────────────

function MemberWorksheets({ userId }) {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(null);

  const load = useCallback(async () => {
    const { data: asgns } = await supabase
      .from('ceds_benchmark_assignments')
      .select('*')
      .eq('member_id', userId)
      .order('created_at');

    setAssignments(asgns ?? []);

    if (asgns?.length) {
      const { data: subs } = await supabase
        .from('ceds_worksheet_submissions')
        .select('*')
        .eq('member_id', userId)
        .neq('status', 'superseded');
      const subMap = {};
      (subs ?? []).forEach(s => { subMap[s.assignment_id] = s; });
      setSubmissions(subMap);
    }

    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color: C.muted, paddingTop: 40, textAlign: 'center' }}>Loading your worksheets…</div>;

  if (!assignments.length) {
    return (
      <div style={{ ...S.card, textAlign: 'center', color: C.muted, padding: '48px 24px', fontSize: 15 }}>
        No benchmark assignments yet. Check back after Meeting 1.
      </div>
    );
  }

  return (
    <div>
      {assignments.map(a => {
        const sub    = submissions[a.id] ?? null;
        const status = sub ? sub.status : 'not_started';
        const isOpen = expanded === a.id;

        return (
          <div key={a.id} style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, marginBottom: 4 }}>
                  {a.ceds_goal}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 8 }}>
                  {a.benchmark_title}
                </div>
                <StatusBadge status={status} />
                {a.due_date && (
                  <span style={{ fontSize: 12, color: C.muted, marginLeft: 12 }}>
                    Due {fmtDate(a.due_date)}
                  </span>
                )}
              </div>
              <button
                onClick={() => setExpanded(isOpen ? null : a.id)}
                style={{ ...S.btn.secondary, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {isOpen ? 'Close' : sub ? 'View / Update' : 'Complete Worksheet →'}
              </button>
            </div>

            {isOpen && (
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${C.rule}` }}>
                <WorksheetForm
                  assignment={a}
                  existingSubmission={sub}
                  memberId={userId}
                  onSubmit={() => { load(); setExpanded(null); }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Admin view ───────────────────────────────────────────────────────────────

function Field({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: highlight ? C.navy : C.muted, fontWeight: highlight ? 700 : 400 }}>
        {value}
      </div>
    </div>
  );
}

function AdminWorksheets({ userId }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('all');
  const [expanded, setExpanded]       = useState(null);
  const [notes, setNotes]             = useState({});
  const [savingNote, setSavingNote]   = useState(null);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('ceds_worksheet_submissions')
      .select(`
        *,
        ceds_council_members ( full_name, county, organization ),
        ceds_benchmark_assignments ( benchmark_title, ceds_goal )
      `)
      .neq('status', 'superseded')
      .order('submitted_at', { ascending: false });
    setSubmissions(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function markReviewed(sub) {
    await supabase.from('ceds_worksheet_submissions').update({
      status:      'reviewed',
      reviewed_at: new Date().toISOString(),
      reviewed_by: userId,
    }).eq('id', sub.id);
    load();
  }

  async function saveNote(subId) {
    setSavingNote(subId);
    await supabase.from('ceds_worksheet_submissions')
      .update({ admin_notes: notes[subId] ?? '' })
      .eq('id', subId);
    setSavingNote(null);
    load();
  }

  function exportCSV() {
    const cols = [
      'member_name', 'county', 'organization', 'benchmark_title', 'ceds_goal',
      'proposed_number', 'proposed_reasoning', 'biggest_risk', 'geographic_note',
      'contacts_consulted', 'what_learned', 'submitted_at', 'status',
    ];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = submissions.map(s => [
      s.ceds_council_members?.full_name,
      s.ceds_council_members?.county,
      s.ceds_council_members?.organization,
      s.ceds_benchmark_assignments?.benchmark_title,
      s.ceds_benchmark_assignments?.ceds_goal,
      s.proposed_number,
      s.proposed_reasoning,
      s.biggest_risk,
      s.geographic_note,
      s.contacts_consulted,
      s.what_learned,
      s.submitted_at,
      s.status,
    ].map(esc).join(','));
    const csv  = [cols.join(','), ...rows].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `ceds-worksheets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const filtered = filter === 'all' ? submissions : submissions.filter(s => s.status === filter);

  if (loading) return <div style={{ color: C.muted, paddingTop: 40, textAlign: 'center' }}>Loading submissions…</div>;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', 'submitted', 'reviewed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            ...S.btn.secondary,
            fontSize: 13, padding: '6px 14px',
            background: filter === f ? C.navy : 'transparent',
            color:      filter === f ? C.white : C.navy,
          }}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button onClick={exportCSV} style={{ ...S.btn.gold, fontSize: 13, padding: '6px 16px', marginLeft: 'auto' }}>
          Export CSV ↓
        </button>
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: C.muted, padding: '40px 24px' }}>
          No submissions yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.rule}` }}>
                {['Member', 'County', 'Benchmark', 'Submitted', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '8px 12px',
                    color: C.navy, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(sub => {
                const isOpen = expanded === sub.id;
                return (
                  <React.Fragment key={sub.id}>
                    <tr style={{ borderBottom: `1px solid ${C.rule}`, background: isOpen ? C.cream : C.white }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: C.navy }}>
                        {sub.ceds_council_members?.full_name}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.muted }}>
                        {sub.ceds_council_members?.county}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.navy }}>
                        {sub.ceds_benchmark_assignments?.benchmark_title}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.muted, whiteSpace: 'nowrap' }}>
                        {fmtDate(sub.submitted_at)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <StatusBadge status={sub.status} />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => setExpanded(isOpen ? null : sub.id)}
                            style={{ ...S.btn.secondary, fontSize: 12, padding: '4px 10px' }}
                          >
                            {isOpen ? 'Close' : 'View'}
                          </button>
                          {sub.status === 'submitted' && (
                            <button
                              onClick={() => markReviewed(sub)}
                              style={{ ...S.btn.primary, fontSize: 12, padding: '4px 10px' }}
                            >
                              Mark Reviewed
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ padding: '20px 24px', background: C.cream, borderBottom: `2px solid ${C.rule}` }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                            <Field label="CEDS Goal"        value={sub.ceds_benchmark_assignments?.ceds_goal} />
                            <Field label="Proposed Target"  value={sub.proposed_number} highlight />
                            <Field label="Reasoning"        value={sub.proposed_reasoning} />
                            <Field label="Biggest Risk"     value={sub.biggest_risk} />
                            <Field label="Who Consulted"    value={sub.contacts_consulted} />
                            <Field label="What Learned"     value={sub.what_learned} />
                            <Field label="Geographic Note"  value={sub.geographic_note} />
                            <Field label="Organization"     value={sub.ceds_council_members?.organization} />
                          </div>
                          <div>
                            <label style={S.label}>Admin Notes</label>
                            <textarea
                              value={notes[sub.id] ?? sub.admin_notes ?? ''}
                              onChange={e => setNotes(n => ({ ...n, [sub.id]: e.target.value }))}
                              rows={3}
                              style={{ ...S.input, resize: 'vertical', marginBottom: 8 }}
                            />
                            <button
                              onClick={() => saveNote(sub.id)}
                              disabled={savingNote === sub.id}
                              style={{ ...S.btn.secondary, fontSize: 13, padding: '6px 14px' }}
                            >
                              {savingNote === sub.id ? 'Saving…' : 'Save Notes'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function WorksheetsPage() {
  const [member, setMember]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [adminView, setAdminView] = useState(true);

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data: m } = await supabase
        .from('ceds_council_members')
        .select('role, full_name')
        .eq('id', session.user.id)
        .maybeSingle();
      setMember({ ...m, id: session.user.id });
      setLoading(false);
    }
    init();
  }, []);

  if (loading) return <div style={{ color: C.muted, paddingTop: 60, textAlign: 'center' }}>Loading…</div>;

  const isAdmin = member?.role === 'padd_admin';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ color: C.navy, margin: '0 0 4px', fontSize: '1.5rem' }}>Drop Box</h2>
          <p style={{ color: C.muted, margin: 0, fontSize: 14 }}>
            {!isAdmin
              ? 'Submit your benchmark assignments and reference materials here.'
              : adminView
                ? 'All council member worksheet submissions.'
                : 'Submit your benchmark assignments and reference materials here.'}
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', border: `1.5px solid ${C.rule}`, borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
            {[{ key: true, label: 'Admin View' }, { key: false, label: 'Member View' }].map(({ key, label }) => (
              <button key={String(key)} onClick={() => setAdminView(key)} style={{
                background:  adminView === key ? C.navy : 'transparent',
                color:       adminView === key ? C.white : C.muted,
                border:      'none',
                padding:     '7px 16px',
                fontSize:    13,
                fontWeight:  adminView === key ? 600 : 400,
                cursor:      'pointer',
              }}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {isAdmin && adminView
        ? <AdminWorksheets userId={member.id} />
        : <MemberWorksheets userId={member.id} />}
    </div>
  );
}
